import { useContext, useEffect, useState } from "react";
import "../../../../styles/workspace_styles/TenantAdministratorStyles/Dashboards/ManageDashboardStates.css";
import { DashboardContext } from "./DashboardProvider";
import { GenerateDashboardStateId } from "../../../../functions/GenerateDashboardStateId";
import { useParams } from "react-router-dom";
import { AppContext } from "../../../AppProvider";
import { isArray } from "chart.js/helpers";
import DeleteDashboardState from "./DeleteDashboardState";
import NewDashboardState from "./NewDashboardState";
import EditDashboardState from "./EditDashboardState";
import { thingsBoardMicroserviceUrl } from "../../../../microservices/thingsboard_microservice";
const DashboardStatesTable = ({
    dashboardStates,
    setDashboardStates,
    setSelectedEditDashboardState,
    setSelectedDeleteDashboardState,
    searchMode,
    searchString}) =>{
    const {user} = useContext(AppContext);
    const { tenantId, dashboardId } = useParams();
    const duplicateDashboardState = async(baseName) =>{
        const duplicateStateId = await GenerateDashboardStateId(tenantId,dashboardId,user?.access_token);
        const matchingCopies = dashboardStates
            .map(state => state.name)
            .filter(name => 
                name === `${baseName} - copy` || 
                name.startsWith(`${baseName} - copy`)
            );

        let newName;
        if (!matchingCopies.includes(`${baseName} - copy`)) {
            newName = `${baseName} - copy`;
        } else {
            const suffixes = matchingCopies
                .map(name => {
                    const match = name.match(new RegExp(`^${baseName} - copy(\\d+)$`));
                    return match ? parseInt(match[1], 10) : null;
                })
                .filter(n => n !== null);

            const nextIndex = suffixes.length > 0 ? Math.max(...suffixes) + 1 : 1;
            newName = `${baseName} - copy${nextIndex}`;
        }
        const duplicateState ={
            id:duplicateStateId,
            name:newName,
            rootState:false,
            showTitle:true
        };
        setDashboardStates(prev => [...prev,duplicateState]);
    };
    return (
        <div className="dashboard-states-table-container">
            <table className="dashboard-states-table">
                <thead>
                    <tr>
                        <th key="name">Name</th>
                        <th key="state-id" style={{width:'340px'}}>State Id</th>
                        <th key="root-state" style={{width:'80px'}}>Root state</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {dashboardStates?.filter(dashboardState => !searchMode || dashboardState?.name?.toLowerCase().includes(searchString.toLowerCase()))
                    .map(dashboardState =>{
                        return(
                            <tr key={dashboardState.id}>
                                <td>{dashboardState.name}</td>
                                <td>{dashboardState.id}</td>
                                <td className="root-state-td">
                                    <input type="checkbox" checked={dashboardState.rootState} readOnly></input>
                                </td>
                                <td>
                                    <button title="Edit dashboard state" onClick={() => {
                                        setSelectedEditDashboardState(dashboardState);
                                    }}>
                                        <img src={`${process.env.PUBLIC_URL}/images/edit-icon.png`} alt="Edit"/>
                                    </button>
                                    <button title="Duplicate state"
                                        onClick={async() => await duplicateDashboardState(dashboardState.name)}>
                                        <img src={`${process.env.PUBLIC_URL}/images/duplicate-icon.png`} alt="Duplicate" />
                                    </button>
                                    <button title="Delete dashboard state" style={{visibility:dashboardState.rootState? 'hidden':'visible'}}
                                        onClick={() => setSelectedDeleteDashboardState(dashboardState)}>
                                        <img src={`${process.env.PUBLIC_URL}/images/trash-can-icon.png`} alt="Delete"/>
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
const ManageDashboardStates = () =>{
    const {dashboard,setDashboard,
    showManageDashboardStates, setShowManageDashboardStates} = useContext(DashboardContext);
    const [dashboardStates,setDashboardStates] = useState([]);
    const [showNewDashboardStateWindow,setShowNewDashboardStateWindow] = useState(false);
    const [selectedEditDashboardState,setSelectedEditDashboardState] = useState(null);
    const [selectedDeleteDashboardState,setSelectedDeleteDashboardState] = useState(null);
    const [searchMode,setSearchMode] = useState(false);
    const [searchString,setSearchString] = useState('');
    const { tenantId, dashboardId } = useParams();
    const {user} = useContext(AppContext);
    const changesAvailable = () =>{
        if(!dashboard 
            ||!isArray(dashboard.dashboardStates)
            ||!isArray(dashboardStates)) return false;
        if (dashboard.dashboardStates?.length !== dashboardStates?.length) return true;
        const sortById = arr => [...arr].sort((a,b) => a.id.localeCompare(b.id));
        const sortedOriginal = sortById(dashboard.dashboardStates);
        const sortedCurrent = sortById(dashboardStates);
        return JSON.stringify(sortedOriginal) !== JSON.stringify(sortedCurrent);
    };
    const discardDashboardStatesChanges = () =>{
        setDashboardStates(dashboard?.dashboardStates);
        setShowManageDashboardStates(false);
    };
    const saveDashboardStateChanges = async() =>{
        try{
            const updatedDashboard = {
                ...dashboard,
                dashboardStates:dashboardStates
            };
            const url = `${thingsBoardMicroserviceUrl}/tenants/profiles/${tenantId}/dashboards/${dashboardId}`;
            const response = await fetch(url,{
                method:'PUT',
                headers:{
                    'Content-Type':'application/json',
                    'Authorization':`Bearer ${user.access_token}`
                },
                body:JSON.stringify(updatedDashboard)
            });
            if(response.ok){
                const responseData = await response.json();
                setDashboard(responseData);
            }
            else throw new Error(`Response code: ${response.status}`);
        }
        catch(error){
            console.error('An error occurred when updating dashboard state changes',error);
        }
        discardDashboardStatesChanges();
    };
    useEffect(() =>{
        if(dashboard && dashboard.dashboardStates) setDashboardStates(dashboard.dashboardStates)
    },[dashboard]);
    if(!showManageDashboardStates) return null;
    return(
        <div className="manage-dashboard-states-parent-container">
            <div className="manage-dashboard-states-container">
                <div className="manage-dashboard-states-header">
                    <h3>Manage dashboard states</h3>
                    <p onClick={() => discardDashboardStatesChanges()}>&#10006;</p>
                </div>
                <div className="manage-dashboard-states-body">
                    <div className="manage-dashboard-states-tool-bar" style={{display:searchMode? 'none':'flex'}}>
                        <h4>Dashboard states</h4>
                        <div>
                            <button
                                className="add-new-dashboard-state-button"
                                title="Add dashboard state"
                                onClick={() => {
                                    setShowNewDashboardStateWindow(true);
                                }}>
                                <img src={`${process.env.PUBLIC_URL}/images/add-icon.png`} alt="Add"/>
                            </button>
                            <button
                                className="search-dashboard-state-button"
                                title="Search"
                                onClick={() => setSearchMode(true)}>
                                <img src={`${process.env.PUBLIC_URL}/images/search-icon-coloured.png`} alt="Search"/>
                            </button>
                        </div>
                    </div>
                    <div className="manage-dashboard-states-search-bar" style={{display:searchMode? 'flex':'none'}}>
                        <img src={`${process.env.PUBLIC_URL}/images/search-icon-coloured.png`} alt="Search"/>
                        <input
                            placeholder="Search dashboard states"
                            value={searchString}
                            onChange={(e) => setSearchString(e.target.value)}
                        />
                        <p onClick={() => setSearchMode(false)}>&#10006;</p>
                    </div>
                    <DashboardStatesTable
                        dashboardStates={dashboardStates}
                        setDashboardStates={setDashboardStates}
                        setSelectedEditDashboardState={setSelectedEditDashboardState}
                        setShowManageDashboardStates={setShowManageDashboardStates}
                        setSelectedDeleteDashboardState={setSelectedDeleteDashboardState}
                        searchMode={searchMode}
                        searchString={searchString}
                    />
                </div>
                <div className="manage-dashboard-states-footer">
                    <button className="cancel-button" onClick={() => discardDashboardStatesChanges()}>
                        Cancel
                    </button>
                    <button className={`save-button ${changesAvailable() ? 'active':'inactive'}`}
                        onClick={async() =>{
                            if(changesAvailable()) await saveDashboardStateChanges();
                        }}>Save</button>
                </div>
            </div>
            <NewDashboardState
                showNewDashboardStateWindow={showNewDashboardStateWindow}
                setShowNewDashboardStateWindow={setShowNewDashboardStateWindow}
                dashboardStates={dashboardStates}
                setDashboardStates={setDashboardStates}
            />
            <EditDashboardState
                selectedEditDashboardState={selectedEditDashboardState}
                setSelectedEditDashboardState={setSelectedEditDashboardState}
                dashboardStates={dashboardStates}
                setDashboardStates={setDashboardStates}
            />
            <DeleteDashboardState
                selectedDeleteDashboardState={selectedDeleteDashboardState}
                setSelectedDeleteDashboardState={setSelectedDeleteDashboardState}
                setDashboardStates={setDashboardStates}
            />
        </div>
    );
}
export default ManageDashboardStates;